import useRolePermission from "entities/auth/hooks/useRolePermission";
import { useCreatePageMutation } from "entities/pages/mutations/useCreatePageMutation";
import { useUpdatePageMutation } from "entities/pages/mutations/useUpdatePageMutation";
import { usePageDetailsQuery } from "entities/pages/queries/usePageDetailsQuery";
import { getPagesListByBrandQueryData } from "entities/pages/queries/usePagesListByBrandQuery";
import { PageDataToCreate, PageIcon } from "entities/pages/types";
import React, { ChangeEvent, FC, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import useInputState, { InputHookResult } from "shared/hooks/useInputState";
import { Translations } from "shared/types/languages";
import { createContext } from "shared/utils/context";
import { usePagesPage } from "../../../context";
import {
  ManagedTranslations,
  PageDetailsProviderProps,
  PageDetailsTabs,
  PageDetailsType,
} from "./types";
import { convertPageNameToPath } from "./utils";

const [Provider, usePageDetails] = createContext<PageDetailsType>({
  name: "pageDetailsContext",
  hookName: "usePageDetails",
  providerName: "pageDetailsProvider",
});

const PageDetailsProvider: FC<PageDetailsProviderProps> = ({ children }) => {
  const { allowed: hasReadAccess } = useRolePermission("brandsAndPages:read");
  const { allowed: hasReadTranslationsAccess } = useRolePermission(
    "brandsAndPages:translations:read",
  );
  const { id } = useParams();
  const [selectedTab, setSelectedTab] = useState(PageDetailsTabs.General);
  const brandId = String(id);
  const { pageDrawer } = usePagesPage();
  const { drawerDetails } = pageDrawer;
  const [pageId, setPageId] = useState<string>();
  const pageName: InputHookResult = useInputState("pageName", "");
  const pageWebTitle: InputHookResult = useInputState("pageWebTitle", "");
  const pageDescription: InputHookResult = useInputState("pageDescription", "");
  const [errors, setErrors] = useState<string>("");
  const [icon, setIcon] = useState<PageIcon | null>(null);
  const [showInBurgerMenu, setShowInBurgerMenu] = useState(true);
  const [showInDesktopHeaderMenu, setShowInDesktopHeaderMenu] = useState(true);
  const [
    showInMobileBurgerMenuQuickLinks,
    setShowInMobileBurgerMenuQuickLinks,
  ] = useState(true);
  const [translations, setTranslations] = useState<Translations | undefined>();
  const [hasSearch, setHasSearch] = useState(true);

  const pageNameValue = pageName?.input.value.trim();
  const pageWebTitleValue = pageWebTitle?.input.value.trim();
  const descriptionValue = pageDescription?.input.value;

  const onClosePageDetails = () => {
    setDefaultPage();
    pageDrawer.closeDrawer();
    setPageId(undefined);
    setShowInBurgerMenu(true);
    setShowInDesktopHeaderMenu(true);
    setShowInMobileBurgerMenuQuickLinks(true);
    setHasSearch(false);
    setIcon(null);
  };

  const { data: pageData = null, isLoading } = usePageDetailsQuery({
    brandId,
    pageId: drawerDetails || "",
  });

  const { mutate: updatePageDetails, isPending: isSubmittingPage } =
    useUpdatePageMutation(onClosePageDetails);

  const { mutate: createPageDetails, isPending } =
    useCreatePageMutation(onClosePageDetails);

  const pages = getPagesListByBrandQueryData(brandId);

  const setDefaultPage = () => {
    pageName.setValue("");
    pageWebTitle.setValue("");
    pageDescription.setValue("");
    pageName?.setErrors([]);
    pageWebTitle?.setErrors([]);
    setTranslations(undefined);
    setSelectedTab(PageDetailsTabs.General);
  };

  const disabled = useMemo(
    () =>
      !pageName?.input.value || Boolean(pageName.input.errors.length) || !icon,
    [pageName.input.errors.length, pageName.input.value, icon],
  );

  useEffect(() => {
    if (hasReadAccess) {
      return;
    }

    if (hasReadTranslationsAccess) {
      setSelectedTab(PageDetailsTabs.Translations);
    }
  }, [hasReadAccess, hasReadTranslationsAccess, pageData]);

  useEffect(() => {
    if (pageData) {
      setPageId(pageData.id);

      if (!pageName.input.value) {
        pageName.setValue(pageData.title);
      }

      if (!pageWebTitle.input.value) {
        pageWebTitle.setValue(pageData.webTitle ?? "");
      }

      if (!pageDescription.input.value) {
        pageDescription.setValue(pageData.description ?? "");
      }

      setShowInBurgerMenu(pageData.showInBurgerMenu);
      setShowInDesktopHeaderMenu(pageData.showInDesktopHeaderMenu);
      setShowInMobileBurgerMenuQuickLinks(
        pageData.showInMobileBurgerMenuQuickLinks,
      );
      setTranslations(pageData.translations);
      setHasSearch(Boolean(pageData.hasSearch));
      setIcon(pageData.icon);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageData]);

  const performPageNameError = () => {
    return pageName?.setErrors([
      { key: "pageName", message: "This page already exists" },
    ]);
  };

  const onChangePageName = (e: ChangeEvent<HTMLInputElement>) => {
    pageName?.input.onChange(e);
    pageName?.setErrors([]);
  };

  const onUpdatePage = async (isActive: boolean) => {
    if (pageData && pageId) {
      const translationsWithDefaultValues = {
        ...translations,
        en: {
          ...translations?.en,
          [ManagedTranslations.PageTitle]: pageNameValue,
        },
      };

      updatePageDetails({
        pageData: {
          ...pageData,
          path: convertPageNameToPath(pageNameValue),
          title: pageNameValue,
          webTitle: pageWebTitleValue,
          description: descriptionValue,
          translations: translationsWithDefaultValues,
          isActive,
          showInBurgerMenu,
          showInDesktopHeaderMenu,
          showInMobileBurgerMenuQuickLinks,
          hasSearch,
          icon,
        },
        pageId,
        brandId,
      });
    }
  };

  const onCreatePage = async (isActive: boolean) => {
    const translationsWithDefaultValues = {
      ...translations,
      en: {
        ...translations?.en,
        [ManagedTranslations.PageTitle]: pageNameValue,
      },
    };

    const pageToSubmit: PageDataToCreate = {
      title: pageNameValue,
      webTitle: pageWebTitleValue,
      description: descriptionValue,
      translations: translationsWithDefaultValues,
      isActive,
      path: convertPageNameToPath(pageNameValue),
      order: 0,
      modulesCount: 0,
      isDefaultForUnauthenticated: false,
      isDefaultForAuthenticated: false,
      showInBurgerMenu,
      showInDesktopHeaderMenu,
      showInMobileBurgerMenuQuickLinks,
      hasSearch,
      icon,
    };

    createPageDetails({ data: pageToSubmit, brandId });
  };

  const isDuplicatePage = useMemo(
    () =>
      pages?.some((page) => page.title === pageNameValue && pageId !== page.id),
    [pageId, pageNameValue, pages],
  );

  const onSubmitPage = async ({ isActive }: { isActive: boolean }) => {
    if (disabled) {
      return;
    }

    if (isDuplicatePage) {
      return performPageNameError();
    }

    try {
      if (pageId) {
        await onUpdatePage(isActive);
      } else {
        await onCreatePage(isActive);
      }
    } catch (error) {
      console.error("Error submitting page details:", error);
    }
  };

  const handleSaveDraft = () => onSubmitPage({ isActive: false });

  const handleSaveActive = () => onSubmitPage({ isActive: true });

  const handleSaveTranslations = () => {
    if (pageData && pageId) {
      const translationsWithDefaultValues = {
        ...translations,
        en: {
          ...translations?.en,
          [ManagedTranslations.PageTitle]: pageNameValue,
        },
      };

      updatePageDetails({
        pageData: {
          translations: translationsWithDefaultValues,
        },
        pageId,
        brandId,
      });
    }
  };

  const isSubmitting = isSubmittingPage || isPending;

  const value = {
    selectedTab,
    setSelectedTab,
    translations,
    setTranslations,
    isLoading,
    onClosePageDetails,
    onChangePageName,
    pageName,
    pageWebTitle,
    pageDescription,
    showInBurgerMenu,
    setShowInBurgerMenu,
    showInDesktopHeaderMenu,
    setShowInDesktopHeaderMenu,
    showInMobileBurgerMenuQuickLinks,
    setShowInMobileBurgerMenuQuickLinks,
    onSubmitPage,
    setErrors,
    errors,
    disabled,
    isSubmitting,
    handleSaveDraft,
    handleSaveActive,
    handleSaveTranslations,
    hasSearch,
    setHasSearch,
    icon,
    setIcon,
  };

  return <Provider value={value}>{children}</Provider>;
};

export { PageDetailsProvider, usePageDetails };
